import React, { useMemo } from "react";
import { Layout, Menu, theme, Button, Drawer } from "antd";
import type { MenuProps } from "antd";
import { Link } from "react-router-dom";
import {
  RightOutlined,
  HomeOutlined,
  LeftOutlined,
  PlusOutlined,
  UserOutlined,
  UserAddOutlined,
  ShopOutlined,
  SolutionOutlined,
  FileTextOutlined,
  FileSyncOutlined,
  ToolOutlined,
  QuestionCircleOutlined,
  TeamOutlined,
  MenuOutlined,
  CalendarOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {
  ArrowLeftRight,
  Building,
  Building2,
  KeyRound,
  LandPlot,
  LayoutTemplate,
  LineChart,
} from "lucide-react";
import { lucideMenuIcon } from "~/components/icons";
import { ROUTES } from "~/utils";
import { useTranslation } from "react-i18next";
import { useMediaQuery } from "@mui/material";

interface SideNavigationProps {
  userHasOrganization?: boolean;
}

const SideNavigation: React.FC<SideNavigationProps> = ({
  userHasOrganization = false,
}) => {
  const { t } = useTranslation();
  const { Sider } = Layout;
  const {
    token: { colorBgContainer },
  } = theme.useToken();
  const smallerMenu = useMediaQuery("(max-width: 650px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const [isMenuCollapsed, setIsMenuCollpased] = React.useState(true);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);
  const width = isMobile ? "100%" : smallerMenu ? "190px" : "230px";

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    // Close drawer on mobile when a menu item is clicked (except toggle)
    if (key !== "toggle-key" && isMobile) {
      setIsDrawerOpen(false);
    }
    // Close menu on fullscreen when clicked (legacy behavior for tablet)
    if (key !== "toggle-key" && !isMobile && smallerMenu) {
      setIsMenuCollpased(true);
    }
  };

  const menuItems: MenuProps["items"] = useMemo(() => {
    const items: MenuProps["items"] = [];

    // Only show toggle on desktop
    if (!isMobile) {
      items.push({
        key: "toggle-key",
        icon: isMenuCollapsed ? <RightOutlined /> : <LeftOutlined />,
        label: isMenuCollapsed ? t("menu_item_show") : t("menu_item_hide"),
        onClick: () => {
          setIsMenuCollpased(!isMenuCollapsed);
        },
      });
    }

    // Home
    items.push({
      key: "home",
      icon: <HomeOutlined />,
      label: <Link to="/">{t("menu_item_home")}</Link>,
    });

    items.push({
      key: "finance",
      icon: lucideMenuIcon(LineChart),
      label: t("menu_item_finance"),
      children: [
        {
          key: "finance-overview",
          icon: lucideMenuIcon(LineChart),
          label: <Link to={ROUTES.finance}>{t("menu_item_finance")}</Link>,
        },
        {
          key: "transactions",
          icon: lucideMenuIcon(ArrowLeftRight),
          label: <Link to={ROUTES.payments}>{t("Transactions")}</Link>,
        },
      ],
    });

    // Properties SubMenu
    items.push({
      key: "properties",
      icon: lucideMenuIcon(Building2),
      label: t("Properties"),
      children: [
        {
          key: "add-property",
          icon: <PlusOutlined />,
          label: <Link to={ROUTES.addProperty}>{t("add_property")}</Link>,
        },
        {
          key: "property-list",
          icon: lucideMenuIcon(Building2),
          label: <Link to={ROUTES.propertyTable}>{t("Properties")}</Link>,
        },
      ],
    });

    // Buildings SubMenu
    items.push({
      key: "buildings",
      icon: lucideMenuIcon(Building),
      label: t("Buildings"),
      children: [
        {
          key: "add-building",
          icon: <PlusOutlined />,
          label: <Link to={ROUTES.addBuilding}>{t("Add_Building")}</Link>,
        },
        {
          key: "building-list",
          icon: lucideMenuIcon(Building),
          label: <Link to={ROUTES.buildingTable}>{t("Building_List")}</Link>,
        },
      ],
    });

    items.push({
      key: "units",
      icon: lucideMenuIcon(KeyRound),
      label: t("Units"),
      children: [
        {
          key: "add-unit",
          icon: <PlusOutlined />,
          label: <Link to={ROUTES.addUnit}>{t("Add_Unit")}</Link>,
        },
        {
          key: "unit-list",
          icon: lucideMenuIcon(KeyRound),
          label: <Link to={ROUTES.unitTable}>{t("Units")}</Link>,
        },
      ],
    });

    items.push({
      key: "parcels",
      icon: lucideMenuIcon(LandPlot),
      label: t("Parcel_List"),
      children: [
        {
          key: "add-parcel",
          icon: <PlusOutlined />,
          label: <Link to={ROUTES.addParcel}>{t("Add_Parcel")}</Link>,
        },
        {
          key: "parcel-list",
          icon: lucideMenuIcon(LandPlot),
          label: <Link to={ROUTES.parcelTable}>{t("Parcel_List")}</Link>,
        },
      ],
    });

    // Tenants SubMenu
    items.push({
      key: "tenants",
      icon: <UserOutlined />,
      label: t("Tenant"),
      children: [
        {
          key: "add-tenant",
          icon: <UserAddOutlined />,
          label: <Link to={ROUTES.addTenant}>{t("Add_Tenant")}</Link>,
        },
        {
          key: "tenant-list",
          icon: <UserOutlined />,
          label: <Link to={ROUTES.tenantTable}>{t("Tenant_List")}</Link>,
        },
      ],
    });

    // Lease SubMenu
    items.push({
      key: "lease",
      icon: <SolutionOutlined />,
      label: t("Lease"),
      children: [
        {
          key: "lease-list",
          icon: <FileTextOutlined />,
          label: <Link to={ROUTES.leaseTable}>{t("Lease")}</Link>,
        },
        {
          key: "lease-updates",
          icon: <FileSyncOutlined />,
          label: <Link to={ROUTES.leaseUpdate}>{t("Lease_Updates")}</Link>,
        },
        {
          key: "stays",
          icon: <CalendarOutlined />,
          label: <Link to={ROUTES.stays}>{t("Stay_list")}</Link>,
        },
      ],
    });

    // Amenities
    items.push({
      key: "amenities",
      icon: <ShopOutlined />,
      label: <Link to={ROUTES.amenity}>{t("Amenities")}</Link>,
    });

    // Unit plans
    items.push({
      key: "unit-plans",
      icon: lucideMenuIcon(LayoutTemplate),
      label: t("Unit_Plans"),
      children: [
        {
          key: "add-unit-plan",
          icon: <PlusOutlined />,
          label: <Link to={ROUTES.addUnitPlan}>{t("Add_Unit_Plan")}</Link>,
        },
        {
          key: "unit-plan-list",
          icon: lucideMenuIcon(LayoutTemplate),
          label: <Link to={ROUTES.unitPlan}>{t("Unit_Plans")}</Link>,
        },
      ],
    });

    // Service Requests
    items.push({
      key: "service-requests",
      icon: <ToolOutlined />,
      label: <Link to={ROUTES.serviceRequest}>{t("Service_Requests")}</Link>,
    });

    // Help
    items.push({
      key: "help",
      icon: <QuestionCircleOutlined />,
      label: <Link to="/help">{t("Help")}</Link>,
    });

    // Organizations SubMenu
    const organizationChildren: MenuProps["items"] = [
      {
        key: "organization-list",
        icon: <TeamOutlined />,
        label: <Link to={ROUTES.organization}>{t("Organization")}</Link>,
      },
    ];

    // Only show "Create Organization" if user doesn't have an organization
    if (!userHasOrganization) {
      organizationChildren.push({
        key: "add-organization",
        icon: <PlusOutlined />,
        label: <Link to={ROUTES.newOrganization}>{t("Add_Organization")}</Link>,
      });
    }

    items.push({
      key: "organizations",
      icon: <TeamOutlined />,
      label: t("Organizations"),
      children: organizationChildren,
    });

    return items;
  }, [isMobile, isMenuCollapsed, userHasOrganization, t]);

  const menuContent = (
    <Menu
      mode="inline"
      defaultSelectedKeys={["home"]}
      style={{
        borderRight: 0,
        height: "100%",
      }}
      onClick={handleMenuClick}
      items={menuItems}
    />
  );

  // Mobile: Floating button + Drawer
  if (isMobile) {
    return (
      <>
        {/* Floating Menu Button */}
        <Button
          type="primary"
          icon={<MenuOutlined />}
          onClick={() => setIsDrawerOpen(true)}
          style={{
            position: "fixed",
            left: 12,
            top: 72, // Right under top nav (64px header + 8px gap)
            zIndex: 1000,
            width: 40,
            height: 40,
            borderRadius: "50%",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            opacity: 0.7,
          }}
          title={t("menu_item_show")}
        />

        {/* Drawer Menu */}
        <Drawer
          title={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <MenuOutlined />
              <span>{t("Menu") || "Menu"}</span>
            </div>
          }
          placement="left"
          onClose={() => setIsDrawerOpen(false)}
          open={isDrawerOpen}
          width="80%"
          styles={{
            body: { padding: 0, overflow: "auto" },
          }}
          closeIcon={<CloseOutlined />}
        >
          {menuContent}
        </Drawer>
      </>
    );
  }

  // Desktop: Standard Sider
  return (
    <Sider
      width={width}
      collapsedWidth={50}
      style={{
        background: colorBgContainer,
        borderRight: "1px solid grey",
        padding: 0,
        marginLeft: 0,
        minHeight: 0,
        overflow: "auto",
        scrollbarWidth: "thin",
      }}
      collapsible={true}
      collapsed={isMenuCollapsed}
      reverseArrow={false}
      trigger={null}
    >
      {menuContent}
    </Sider>
  );
};

export default SideNavigation;
